import { useCallback, useEffect, useRef, useState } from "react";

const GUARD_STATE_KEY = "unsavedChangesGuard";

// Whether the current history entry is a guard entry
const isOnHistoryEntry = () => !!window.history.state?.[GUARD_STATE_KEY];

// Cmd/Ctrl+R (also with Shift) and F5
const isReloadShortcut = (event) =>
  event.key === "F5" ||
  ((event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === "r");

// Active guards, latest last; only the top one handles reload shortcuts
const activeGuards = [];

// The last opened guard
const getTopGuard = () => activeGuards[activeGuards.length - 1];

// Unsaved changes of all open guards
const getAllUnsavedChanges = () =>
  activeGuards.flatMap((guard) => guard.getUnsavedChanges());

// The popup's own reload: skip the browser dialog and reload after dropping history entries
let isReloading = false;
// Ignores the Back a guard triggers when dropping its entry on unmount
let skipNextPop = false;

// Browser Back: the topmost guard with an entry takes it, else each handles it as a plain Back
const handlePopState = () => {
  if (isReloading) {
    window.location.reload();
    return;
  }
  if (skipNextPop) {
    skipNextPop = false;
    return;
  }

  const guardWithEntry = [...activeGuards]
    .reverse()
    .find((guard) => guard.hasHistoryEntry());
  if (guardWithEntry) {
    guardWithEntry.onHistoryPop();
    return;
  }

  activeGuards.forEach((guard) => guard.onPlainPop());
};

// Reload shortcut with unsaved changes: the top guard shows the popup instead
const handleKeyDown = (event) => {
  if (!isReloadShortcut(event)) return;

  const changes = getAllUnsavedChanges();
  if (!changes.length) return;

  event.preventDefault();
  getTopGuard()?.onReloadShortcut(changes);
};

// Reload button, tab close or typed URL: asks for the browser's own dialog
const handleBeforeUnload = (event) => {
  if (isReloading || !getAllUnsavedChanges().length) return;

  event.preventDefault();
  event.returnValue = "";
};

// Adds a guard; the first one attaches the listeners
const registerGuard = (guard) => {
  if (!activeGuards.length) {
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("beforeunload", handleBeforeUnload);
  }
  activeGuards.push(guard);
};

// Removes a guard; the last one detaches the listeners
const unregisterGuard = (guard) => {
  activeGuards.splice(activeGuards.indexOf(guard), 1);
  if (!activeGuards.length) {
    window.removeEventListener("popstate", handlePopState);
    window.removeEventListener("keydown", handleKeyDown, true);
    window.removeEventListener("beforeunload", handleBeforeUnload);
  }
};

// Drops the guards' history entries first, so none is left after the reload
const reloadPage = () => {
  isReloading = true;
  // In case the reload is cancelled
  setTimeout(() => {
    isReloading = false;
  }, 2000);

  const entries = activeGuards.filter((guard) =>
    guard.hasHistoryEntry()
  ).length;
  if (entries) {
    window.history.go(-entries);
  } else {
    window.location.reload();
  }
};

/**
 * Asks before a modal closes or the page reloads with unsaved changes. Reload shortcuts show the popup; the reload
 * button and tab close show the browser's dialog.
 *
 * @param {Object} options
 * @param {boolean} options.isOpen - Whether the modal is open.
 * @param {Function} options.onClose - Closes the modal.
 * @param {boolean} options.hasUnsavedChanges - When true, a same-URL history entry is pushed so browser Back opens
 *   the popup.
 * @param {Function} options.getUnsavedChanges - Returns the names of what is unsaved, read on leaving.
 * @param {boolean} [options.closeOnHistoryPop=false] - Close on browser Back when nothing is unsaved.
 */
const useUnsavedChanges = ({
  isOpen,
  onClose,
  hasUnsavedChanges,
  getUnsavedChanges,
  closeOnHistoryPop = false,
}) => {
  const [unsavedChanges, setUnsavedChanges] = useState(null);

  const hasHistoryEntryRef = useRef(false);
  const isLeavingRef = useRef(false);
  const isDiscardedRef = useRef(false);
  const isReloadRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const getUnsavedChangesRef = useRef(getUnsavedChanges);
  getUnsavedChangesRef.current = getUnsavedChanges;
  const closeOnHistoryPopRef = useRef(closeOnHistoryPop);
  closeOnHistoryPopRef.current = closeOnHistoryPop;

  // Adds the same-URL entry that browser Back pops instead of leaving
  const pushHistoryEntry = useCallback(() => {
    // A fresh tab has nowhere to go back to, and the entry would stop Close closing it
    if (hasHistoryEntryRef.current || window.history.length <= 1) return;

    window.history.pushState(
      { ...window.history.state, [GUARD_STATE_KEY]: true },
      "",
      window.location.href
    );
    hasHistoryEntryRef.current = true;
  }, []);

  // Arms browser Back once there are unsaved changes (and again after Keep Editing)
  useEffect(() => {
    if (isOpen && hasUnsavedChanges && !unsavedChanges) pushHistoryEntry();
  }, [isOpen, hasUnsavedChanges, unsavedChanges, pushHistoryEntry]);

  // Drops the history entry first so the close goes back to the real previous page
  const leave = useCallback(() => {
    if (isLeavingRef.current) return;

    if (!hasHistoryEntryRef.current) {
      onCloseRef.current();
      return;
    }

    isLeavingRef.current = true;
    window.history.back();
  }, []);

  // Back popped this guard's entry: close when leaving, else show the popup or close
  const handleHistoryPop = useCallback(() => {
    hasHistoryEntryRef.current = false;

    if (isLeavingRef.current) {
      isLeavingRef.current = false;
      onCloseRef.current();
      return;
    }

    const changes = getUnsavedChangesRef.current();
    if (changes.length) {
      isReloadRef.current = false;
      setUnsavedChanges(changes);
    } else {
      onCloseRef.current();
    }
  }, []);

  // Registers this modal's guard while it's open
  useEffect(() => {
    if (!isOpen) return undefined;

    isDiscardedRef.current = false;
    const guard = {
      getUnsavedChanges: () =>
        isDiscardedRef.current ? [] : getUnsavedChangesRef.current(),
      hasHistoryEntry: () => hasHistoryEntryRef.current,
      onHistoryPop: handleHistoryPop,
      onPlainPop: () => {
        if (closeOnHistoryPopRef.current) {
          onCloseRef.current({ fromHistory: true });
        }
      },
      onReloadShortcut: (changes) => {
        isReloadRef.current = true;
        setUnsavedChanges(changes);
      },
    };
    registerGuard(guard);

    return () => {
      unregisterGuard(guard);

      // Closed another way (e.g. Process): drop the leftover entry
      if (hasHistoryEntryRef.current && isOnHistoryEntry()) {
        hasHistoryEntryRef.current = false;
        if (activeGuards.length) skipNextPop = true;
        window.history.back();
      }
    };
  }, [isOpen, handleHistoryPop]);

  // The modal's own close: popup if anything is unsaved, else leave
  const requestClose = useCallback(() => {
    isReloadRef.current = false;

    const changes = getUnsavedChangesRef.current();
    if (changes.length) {
      setUnsavedChanges(changes);
      return;
    }

    leave();
  }, [leave]);

  // Discard & Leave: reload or leave, whichever opened the popup
  const discardAndLeave = useCallback(() => {
    setUnsavedChanges(null);
    isDiscardedRef.current = true;

    if (isReloadRef.current) {
      reloadPage();
      return;
    }

    leave();
  }, [leave]);

  // Keep Editing: hides the popup; the effect above re-arms browser Back
  const keepEditing = useCallback(() => {
    isReloadRef.current = false;
    setUnsavedChanges(null);
  }, []);

  return { unsavedChanges, requestClose, discardAndLeave, keepEditing };
};

export default useUnsavedChanges;
